import { useEffect, useMemo, useState } from 'react'

import { LDMultiKindContext, ReactNativeLDClient } from '@launchdarkly/react-native-client-sdk'
import { LDClient, LDMultiKindContext as LDMultiKindContextWeb } from 'launchdarkly-react-client-sdk'

import { ExperimentDataParams } from '../../experiments/hooks/useSendExperimentData'
import { ExperimentUUIDs, ExperimentUUIDToFlagName } from '../../experiments/types'
import { getMemberRecommendedBadgeData } from '../utils'

interface useShouldShowMemberRecommendedBadgeArgs {
  isProviderRecommended?: boolean
  isTopProviderInResultsList?: boolean
  isScreenEligibleToDisplayBadge?: boolean
  sendExperimentData?: ({ experimentUUID, joinKeys }: ExperimentDataParams) => void
  isFocused?: boolean
  // PROVREC-5360: Temporary logging to help debug why showMemberRecommendedBadgeFlag is not consistent between page views
  onLogBeforeExperimentRequest?: () => void
  onLogAfterExperimentRequest?: ({
    shouldShowMemberRecommendedBadge,
    isEligibleForMemberRecommendedBadge,
  }: {
    shouldShowMemberRecommendedBadge: boolean
    isEligibleForMemberRecommendedBadge: boolean
  }) => void
  ldClient?: LDClient | ReactNativeLDClient
}

/**
 * Hook to encapsulate logic for when the member-recommended badge should be displayed
 *
 * @param isProviderRecommended - Value of whether the provider is recommended from the backend
 * @param isTopProviderInResultsList - Use for provider list views to show badge only on top provider
 * @param isScreenEligibleToDisplayBadge - Use for profile views to show badge on all recommended providers
 * @param updateLDContext - app-specific instance of function to send LaunchDarkly context
 * @param sendExperimentData - app-specific instance of function to send experiment data to the backend
 * @param isFocused - mobile-specific parameter to know when the screen is focused
 * @param onLogBeforeExperimentRequest - app-specific callback to send SumoLogic logs
 * @param onLogAfterExperimentRequest - app-specific callback to send SumoLogic logs
 * @param ldClient - the launchdarkly client
 */
export const useShouldShowMemberRecommendedBadge = ({
  isProviderRecommended,
  isTopProviderInResultsList = false,
  isScreenEligibleToDisplayBadge = false,
  sendExperimentData,
  isFocused,
  // PROVREC-5360: Temporary logging to help debug why showMemberRecommendedBadgeFlag is not consistent between page views
  onLogBeforeExperimentRequest,
  onLogAfterExperimentRequest,
  ldClient,
}: useShouldShowMemberRecommendedBadgeArgs) => {
  const [isLDContextUpdated, setIsLDContextUpdated] = useState(false)
  const [hasSentExperimentData, setHasSentExperimentData] = useState(false)
  const [showMemberRecommendedBadgeFlag, setShowMemberRecommendedBadgeFlag] = useState<boolean | null>(null)

  const data = useMemo(
    () =>
      showMemberRecommendedBadgeFlag != null
        ? getMemberRecommendedBadgeData({
            showMemberRecommendedBadgeFlag,
            isProviderRecommended,
            isTopProviderInResultsList,
            isScreenEligibleToDisplayBadge,
          })
        : null,
    [isProviderRecommended, isScreenEligibleToDisplayBadge, isTopProviderInResultsList, showMemberRecommendedBadgeFlag],
  )

  useEffect(() => {
    if (
      !isFocused ||
      isLDContextUpdated ||
      isProviderRecommended == null ||
      showMemberRecommendedBadgeFlag != null ||
      ldClient == null
    )
      return

    const onUpdateLDContext = async () => {
      try {
        // Get the current context from the LD client
        const currentContext = ldClient.getContext() as LDMultiKindContextWeb | LDMultiKindContext

        // Update the user context with the new memberRecommendedProviderShown attribute
        // Using spread operators to merge contexts (works for both mobile and web)
        const updatedContext = {
          ...currentContext,
          user: {
            ...((currentContext as any)?.user || {}),
            memberRecommendedProviderShown: isProviderRecommended,
          },
        }

        // Call identify to update the context and wait for it to complete
        await ldClient.identify(updatedContext as any)

        // Retrieve all flags after the context update has completed
        // This ensures we get flag values for the NEW context, not the old one
        const flags = await ldClient.allFlags()

        // Extract the specific flag value from the returned flags object
        const memberRecommendedBadgeFlagName = ExperimentUUIDToFlagName[
          ExperimentUUIDs.ClientTestimonialV1UUID
        ] as string
        const flagValue = flags?.[memberRecommendedBadgeFlagName] ?? null

        // Set flag value FIRST, then mark context as updated
        // This prevents race condition where second effect sees isLDContextUpdated=true but flagValue=null
        setShowMemberRecommendedBadgeFlag(flagValue)
        setIsLDContextUpdated(true)
      } catch {
        // Also reorder in error case for consistency
        setShowMemberRecommendedBadgeFlag(false)
        setIsLDContextUpdated(true)
      }
    }

    onUpdateLDContext()
  }, [isFocused, isLDContextUpdated, isProviderRecommended, ldClient, showMemberRecommendedBadgeFlag])

  useEffect(() => {
    const sendClientTestimonialData = async () => {
      if (!sendExperimentData || !isLDContextUpdated || !isFocused || hasSentExperimentData || !data) return
      if (isProviderRecommended == null || showMemberRecommendedBadgeFlag == null) {
        return
      }
      try {
        onLogBeforeExperimentRequest?.()
      } catch {}

      sendExperimentData({ experimentUUID: ExperimentUUIDs.ClientTestimonialV1UUID })
      setHasSentExperimentData(true)

      try {
        onLogAfterExperimentRequest?.(data)
      } catch {}
    }
    sendClientTestimonialData()
  }, [
    data,
    hasSentExperimentData,
    isFocused,
    isLDContextUpdated,
    isProviderRecommended,
    onLogAfterExperimentRequest,
    onLogBeforeExperimentRequest,
    sendExperimentData,
    showMemberRecommendedBadgeFlag,
  ])

  return data
}
